import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, BookOpen, BarChart3, Users, Settings as SettingsIcon, LogOut, ShieldCheck, GraduationCap, Award, Sparkles, Heart, ShoppingCart, Tag, Send, RotateCcw } from "lucide-react";
import { LOGO_URL } from "../constants";
import { useAuth } from "../contexts/AuthContext";

const navItems = [
    { to: "/dashboard", icon: Home, label: "Dashboard" },
    { to: "/my-courses", icon: GraduationCap, label: "My Courses" },
    { to: "/catalog", icon: BookOpen, label: "Lessons", badge: "24" },
    { to: "/wishlist", icon: Heart, label: "Wishlist" },
    { to: "/cart", icon: ShoppingCart, label: "Cart" },
    { to: "/progress", icon: BarChart3, label: "Progress" },
    { to: "/certificates", icon: Award, label: "Certificates" },
    { to: "/settings", icon: SettingsIcon, label: "Settings" },
];

const adminNavItems = [
    { to: "/admin", icon: Home, label: "Overview" },
    { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/admin/customers", icon: Users, label: "Customers" },
    { to: "/admin/courses", icon: BookOpen, label: "Courses" },
    { to: "/admin/enrollments", icon: GraduationCap, label: "Enrollments" },
    { to: "/admin/certificates", icon: Award, label: "Certificates" },
    { to: "/admin/leads", icon: Sparkles, label: "Leads" },
    { to: "/admin/discounts", icon: Tag, label: "Discounts" },
    { to: "/admin/broadcasts", icon: Send, label: "Broadcasts" },
    { to: "/admin/refunds", icon: RotateCcw, label: "Refunds" },
];

export default function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const isAdmin = location.pathname.startsWith("/admin");
    const isAdminUser = user?.role === "admin";
    const items = isAdmin ? adminNavItems : navItems;

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    // If someone tries to access admin routes without admin role, redirect
    if (isAdmin && !isAdminUser) {
        navigate("/dashboard");
        return null;
    }

    return (
        <aside
            data-testid="app-sidebar"
            style={{
                display: "none",
                flexDirection: "column",
                width: 240, flexShrink: 0,
                height: "100vh", position: "sticky", top: 0,
                background: "#0A0B0E",
                borderRight: "1px solid rgba(255,255,255,0.07)",
                padding: "24px 16px",
            }}
            className="lg-sidebar"
        >
            {/* Logo */}
            <NavLink to="/" data-testid="brand-logo" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, padding: "0 8px" }}>
                <img src={LOGO_URL} alt="Blue Mountain Group" style={{ height: 44, width: "auto", objectFit: "contain" }} />
            </NavLink>

            <NavLink
                to="/"
                data-testid="sidebar-home"
                style={{
                    marginBottom: 24, fontSize: 10, color: "#6b7280",
                    textDecoration: "none", display: "inline-flex",
                    alignItems: "center", gap: 4, padding: "0 8px",
                    fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em",
                    transition: "color 0.2s"
                }}
            >
                ← Back to marketing site
            </NavLink>

            {isAdmin && (
                <div style={{
                    marginBottom: 16, padding: "6px 12px",
                    borderRadius: 99, background: "rgba(124,92,255,0.12)",
                    border: "1px solid rgba(124,92,255,0.3)",
                    color: "#7C5CFF", fontSize: 11, fontWeight: 700,
                    display: "inline-flex", alignItems: "center", gap: 6,
                    alignSelf: "flex-start"
                }}>
                    <ShieldCheck size={12} /> Admin Mode
                </div>
            )}

            {/* Nav */}
            <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
                {items.map((item) => {
                    const Icon = item.icon;
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/admin"}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            style={({ isActive }) => ({
                                display: "flex", alignItems: "center",
                                justifyContent: "space-between", gap: 12,
                                padding: "10px 12px", borderRadius: 12,
                                textDecoration: "none",
                                color: isActive ? "#fff" : "#6b7280",
                                background: isActive ? "rgba(255,255,255,0.07)" : "transparent",
                                border: isActive ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
                                fontWeight: isActive ? 600 : 400,
                                fontSize: 13, transition: "all 0.15s"
                            })}
                        >
                            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <Icon size={16} />
                                {item.label}
                            </span>
                            {item.badge && (
                                <span style={{
                                    fontSize: 10, fontWeight: 800,
                                    padding: "2px 7px", borderRadius: 99,
                                    background: "#7C5CFF", color: "#fff"
                                }}>{item.badge}</span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* Switch to Admin — only shown to admin users */}
            {isAdminUser && (
                <NavLink
                    to={isAdmin ? "/dashboard" : "/admin"}
                    data-testid="mode-switch"
                    style={{
                        marginTop: 12, display: "flex", alignItems: "center",
                        justifyContent: "center", gap: 8,
                        padding: "10px 16px", borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#d1d5db", fontSize: 13, fontWeight: 600,
                        textDecoration: "none", transition: "all 0.2s"
                    }}
                >
                    <ShieldCheck size={15} />
                    {isAdmin ? "Switch to Student" : "Switch to Admin"}
                </NavLink>
            )}

            {/* Advisory widget */}
            <div style={{
                marginTop: 12, padding: 16, borderRadius: 16,
                background: "linear-gradient(135deg, rgba(124,92,255,0.2), rgba(10,11,14,0.8), rgba(207,255,80,0.08))",
                border: "1px solid rgba(255,255,255,0.08)", position: "relative", overflow: "hidden"
            }}>
                <Sparkles size={18} color="#CFFF50" style={{ marginBottom: 8 }} />
                <div style={{ fontWeight: 700, fontSize: 14, color: "#fff", lineHeight: 1.3 }}>Advisory on call</div>
                <p style={{ fontSize: 11, color: "#6b7280", marginTop: 4, lineHeight: 1.5 }}>24/7 expert support from Blue Mountain mentors.</p>
                <button
                    data-testid="advisory-btn"
                    style={{
                        marginTop: 10, width: "100%", padding: "8px 12px",
                        borderRadius: 99, background: "rgba(255,255,255,0.08)",
                        border: "none", color: "#fff", fontSize: 11,
                        fontWeight: 700, cursor: "pointer"
                    }}
                >
                    Get help
                </button>
            </div>

            {/* Logout / Sign in */}
            {user ? (
                <button
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    style={{
                        marginTop: 8, display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 12px", color: "#6b7280", fontSize: 13,
                        background: "transparent", border: "none",
                        cursor: "pointer", transition: "color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#ef4444"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
                >
                    <LogOut size={15} /> Log out
                </button>
            ) : (
                <NavLink
                    to="/login"
                    data-testid="login-link"
                    style={{
                        marginTop: 8, display: "flex", alignItems: "center",
                        justifyContent: "center", gap: 8,
                        padding: "12px 16px", borderRadius: 12,
                        background: "#CFFF50", color: "#0A0B0E",
                        fontWeight: 800, fontSize: 13, textDecoration: "none"
                    }}
                >
                    Sign in
                </NavLink>
            )}
        </aside>
    );
}
